package com.appsyde.shared.presentation.screen.component.message

import androidx.compose.runtime.*
import com.appsyde.contract.clearText.EmailBodyCleaner
import com.appsyde.shared.app.presentation.ticket.SendState
import com.appsyde.shared.modules.ai.domain.model.AiRefineAction
import com.appsyde.shared.modules.ticket.domain.model.Direction
import com.appsyde.shared.modules.ticket.domain.model.MessageAttachment
import com.appsyde.shared.modules.ticket.domain.model.TicketRespondMessage
import com.appsyde.shared.getAccessToken
import com.appsyde.shared.backendBaseUrl
import kotlin.js.js
import com.appsyde.shared.presentation.screen.component.message.helper.bestBody
import com.appsyde.shared.presentation.screen.component.message.helper.formatSmartTime
import com.appsyde.shared.presentation.screen.component.message.helper.authorInitial
import com.appsyde.shared.presentation.style.style.AppStylesControls
import com.appsyde.shared.presentation.style.style.AppStylesMessages
import org.jetbrains.compose.web.ExperimentalComposeWebSvgApi
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.svg.Path
import org.jetbrains.compose.web.svg.Svg
import org.w3c.dom.HTMLElement

private const val VISIBLE_ATTACHMENTS_COLLAPSED = 2

@OptIn(ExperimentalComposeWebSvgApi::class)
@Composable
fun MessageList(
    activeMessages: List<TicketRespondMessage>,
    ticketId: String?,

    answerText: String,
    onAnswerChange: (String) -> Unit,
    onEditingChange: (Boolean) -> Unit,
    /** Réponse générée par le backend (draft IA) pour le bouton « Réponse suggérée ». */
    suggestedReplyDraft: String = "",

    isModified: Boolean,
    isRegenerating: Boolean,

    regenerateAnswer: () -> Unit,
    onRefine: (String, AiRefineAction) -> Unit,
    toneRefineAction: AiRefineAction,
    toneLabel: String,

    sendState: SendState,
    onSend: () -> Unit,
    outboundAttachments: List<com.appsyde.shared.app.presentation.ticket.SelectedOutboundFile> = emptyList(),
    attachmentErrors: List<String> = emptyList(),
    onRemoveAttachment: (String) -> Unit = {},
    onAddAttachment: (com.appsyde.shared.app.presentation.ticket.SelectedOutboundFile) -> Unit = {},
    onSetAttachmentErrors: (List<String>) -> Unit = {},
    onClearAttachmentErrors: () -> Unit = {},
    onClearAllAttachments: () -> Unit = {},
    ) {
    val lastMsg = activeMessages.lastOrNull()
    val awaitingClient = lastMsg?.direction == Direction.OUTBOUND

    val isFirstLoadLoading = (!awaitingClient) && answerText.isBlank() && !isRegenerating
    var expandedAttachmentIds by remember { mutableStateOf(emptySet<String>()) }

    Div(attrs = { classes(AppStylesMessages.messageList) }) {

        activeMessages.forEachIndexed { idx, msg ->
            key(msg.messageId) {
                val isInbound = msg.direction == Direction.INBOUND
                val body = msg.bestBody()

                Div(attrs = { classes(AppStylesMessages.messageRow) }) {

                    if (isInbound) {
                        Div(attrs = { classes(AppStylesMessages.avatarClient) }) { Text(msg.authorInitial()) }
                    } else {
                        Div(attrs = { classes(AppStylesMessages.avatarAI) }) {
                            Svg(
                                viewBox = "0 0 24 24",
                                attrs = {
                                    attr("width", "15")
                                    attr("height", "15")
                                    classes(AppStylesMessages.avatarIconSvg)
                                    attr("fill", "currentColor")
                                }
                            ) { Path(d = "M13 2L3 14h9l-1 8 10-12h-9l1-8z") }
                        }
                    }

                    Div(attrs = { classes(AppStylesMessages.messageColumn) }) {

                        if (isInbound) {
                            InboundMessageMetaWithAttachments(
                                who = "Client",
                                time = formatSmartTime(msg.sentAtMs),
                                attachments = msg.attachments,
                                messageId = msg.messageId,
                                ticketId = ticketId,
                                expandedIds = expandedAttachmentIds,
                                onToggleExpand = { id ->
                                    expandedAttachmentIds = if (id in expandedAttachmentIds) expandedAttachmentIds - id else expandedAttachmentIds + id
                                }
                            )
                        } else {
                            InboundMessageMetaWithAttachments(
                                who = "Vous",
                                time = formatSmartTime(msg.sentAtMs),
                                attachments = msg.attachments,
                                messageId = msg.messageId,
                                ticketId = ticketId,
                                expandedIds = expandedAttachmentIds,
                                onToggleExpand = { id ->
                                    expandedAttachmentIds = if (id in expandedAttachmentIds) expandedAttachmentIds - id else expandedAttachmentIds + id
                                }
                            )
                        }

                    if (isInbound) {
                        val (cleanBody, signature) = EmailBodyCleaner.cleanInboundBodyAndSignatureForDisplay(body, 12_000)
                        val displayBody = EmailBodyCleaner.compactParagraphsForDisplay(cleanBody)
                        var showSignature by remember(msg.messageId) { mutableStateOf(false) }
                        Div(attrs = { classes(AppStylesMessages.bubbleClient, AppStylesMessages.bubbleClientBg) }) {
                            Div(attrs = { classes(AppStylesMessages.bubbleContent, AppStylesMessages.bubbleClientContent) }) {
                                // Un seul bloc : pas de margin-bottom, sauts de ligne naturels du texte (pre-wrap)
                                Text(displayBody.ifBlank { cleanBody })
                                if (signature != null) {
                                    Div(attrs = { classes(AppStylesMessages.bubbleClientSignatureRow) }) {
                                        Button(attrs = {
                                            attr("type", "button")
                                            classes(AppStylesMessages.bubbleClientSignatureToggle)
                                            onClick { showSignature = !showSignature }
                                        }) { Text(if (showSignature) "Masquer la signature" else "…") }
                                        if (showSignature) {
                                            Div(attrs = { classes(AppStylesMessages.bubbleClientSignatureBlock) }) {
                                                Text(signature)
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        Div(attrs = { classes(AppStylesMessages.bubbleAI) }) {
                            Div(attrs = { classes(AppStylesMessages.bubbleContent) }) {
                                Text(body)
                            }
                        }
                    }
                    }
                }
            }
        }

        Div(attrs = { classes(AppStylesMessages.messageRow, AppStylesMessages.messageRowDraft) }) {
            Div(attrs = { classes(AppStylesMessages.messageColumn) }) {
                AiBubbleEditable(
                    text = answerText,
                    onTextChange = onAnswerChange,
                    onEditingChange = onEditingChange,
                    suggestedReplyDraft = suggestedReplyDraft,
                    awaitingClient = awaitingClient,
                    isFirstLoadLoading = isFirstLoadLoading,
                    isRegenerating = isRegenerating,
                    onRegenerate = regenerateAnswer,
                    sendState = sendState,
                    onSend = onSend,
                    onFix = { t -> onRefine(t, AiRefineAction.GRAMMAR) },
                    onPro = { t -> onRefine(t, toneRefineAction) },
                    toneLabel = toneLabel,
                    draftHeaderLabel = "",
                    attachments = outboundAttachments,
                    attachmentErrors = attachmentErrors,
                    onRemoveAttachment = onRemoveAttachment,
                    onAddAttachment = onAddAttachment,
                    onSetAttachmentErrors = onSetAttachmentErrors,
                    onClearAttachmentErrors = onClearAttachmentErrors,
                    onClearAllAttachments = onClearAllAttachments
                )

                AiToolbarBellowBubble(
                    text = answerText,
                    isFirstLoadLoading = isFirstLoadLoading,
                    isRegenerating = isRegenerating,
                    onFix = { text -> onRefine(text, AiRefineAction.GRAMMAR) },
                    onPro = { text -> onRefine(text, toneRefineAction) },
                    onShorter = { text -> onRefine(text, AiRefineAction.SHORTEN) },
                    toneLabel = toneLabel
                )
            }
        }
    }
}

fun typeLabel(mime: String?): String = when {
    mime == null -> "fichier"
    mime.startsWith("image/") -> "image"
    mime == "application/pdf" -> "pdf"
    mime.startsWith("application/") -> "document"
    mime.startsWith("text/") -> "texte"
    else -> "fichier"
}

/** Icône courte par type pour la liste des pièces jointes outbound (PDF, Image, Doc, etc.). */
fun outboundAttachmentIcon(mimeType: String?, filename: String?): String {
    val mime = mimeType?.trim()?.lowercase() ?: ""
    val ext = filename?.substringAfterLast('.', "")?.lowercase() ?: ""
    return when {
        mime == "application/pdf" || ext == "pdf" -> "📄"
        mime.startsWith("image/") || ext in setOf("png", "jpg", "jpeg", "webp") -> "🖼️"
        "word" in mime || ext == "docx" -> "📝"
        "sheet" in mime || "excel" in mime || ext == "xlsx" -> "📊"
        "presentation" in mime || ext == "pptx" -> "📽️"
        mime == "application/zip" || ext == "zip" -> "📦"
        mime.startsWith("audio/") -> "🎵"
        mime.startsWith("video/") -> "🎬"
        mime.startsWith("text/") || ext == "txt" -> "📃"
        else -> "📎"
    }
}

fun formatFileSize(bytes: Long): String {
    if (bytes < 1024) return "$bytes o"
    if (bytes < 1024 * 1024) return "${bytes / 1024} Ko"
    return "${bytes / (1024 * 1024)} Mo"
}
@Composable
private fun InboundMessageMetaWithAttachments(
    who: String,
    time: String,
    attachments: List<MessageAttachment>,
    messageId: String,
    ticketId: String?,
    expandedIds: Set<String>,
    onToggleExpand: (String) -> Unit
) {
    val expanded = messageId in expandedIds
    val visibleCount = if (expanded) attachments.size else VISIBLE_ATTACHMENTS_COLLAPSED
    val visible = attachments.take(visibleCount)
    val remaining = attachments.size - VISIBLE_ATTACHMENTS_COLLAPSED
    val baseUrl = backendBaseUrl().trimEnd('/')

    Div(attrs = { classes(AppStylesMessages.attachmentMetaLine) }) {
        Span(attrs = { classes(AppStylesMessages.clientEmail) }) { Text(who) }
        Span(attrs = { classes(AppStylesMessages.attachmentSeparator) }) { Text("·") }
        Span(attrs = { classes(AppStylesMessages.clientMetaTime) }) { Text(time) }

        if (attachments.isNotEmpty()) {
            Span(attrs = { classes(AppStylesMessages.attachmentSeparator) }) { Text("·") }
            Span(attrs = { classes(AppStylesMessages.attachmentIcon) }) { Text("📎") }

            visible.forEachIndexed { index, att ->
                if (index > 0) Span(attrs = { classes(AppStylesMessages.attachmentSeparator) }) { Text("·") }
                val accessToken = getAccessToken()
                val url = if (ticketId != null) {
                    val base = "$baseUrl/tickets/$ticketId/messages/$messageId/attachments/${att.id}"
                    if (!accessToken.isNullOrBlank()) base + "?access_token=" + (js("encodeURIComponent") as (String) -> String)(accessToken) else base
                } else null
                if (url != null) {
                    A(url, {
                        classes(AppStylesMessages.attachmentLink)
                        attr("target", "_blank")
                        attr("rel", "noopener noreferrer")
                        if (!att.mimeType.startsWith("image/") && att.mimeType != "application/pdf") {
                            attr("download", att.filename)
                        }
                    }) {

                        Text(typeLabel(att.mimeType)) }
                } else {
                    Span(attrs = { classes(AppStylesMessages.attachmentLink) }) { Text(att.filename) }
                }
            }

            if (remaining > 0 && !expanded) {
                 Button(attrs = {
                    classes(AppStylesMessages.attachmentPill)
                    attr("type", "button")
                    onClick { onToggleExpand(messageId) }
                }) { Text("+$remaining") }
            }
            if (expanded && remaining > 0) {
                Span(attrs = { classes(AppStylesMessages.attachmentSeparator) }) { Text("·") }
                Button(attrs = {
                    classes(AppStylesMessages.attachmentPill)
                    attr("type", "button")
                    onClick { onToggleExpand(messageId) }
                }) { Text("−") }
            }
        }
    }
}
